import {type ClassValue, clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';

import type {TransactionStatus} from '@/dintero/shared';
import type {Transaction, TransactionEventName} from '@/dintero/transaction';
import type {OrderPartStatus} from '@/prisma/generated/client';

export type LineStatus = TransactionEventName; // alias for convenience
export type LineStatusMap = Map<string, LineStatus>; // line id : status map
export type PaymentStatus = TransactionStatus; // alias for convenience

// TODO: use tailwind config for this eventually, extracted all the simple colors to be used in this file for now
const adminColors = {
	danger: 'bg-red-100 text-red-800 hover:bg-red-100',
	tentative: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
	success: 'bg-green-100 text-green-800 hover:bg-green-100',
	progress: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
	info: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
};

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Background for the row of the currently open order/customer in a list table. A muted yellow,
// kept opaque (no transparency) since it's also used on sticky columns that must stay solid
// while other columns scroll beneath them.
export const SELECTED_ROW_BACKGROUND = 'bg-yellow-100 dark:bg-yellow-950';

export const getShippingStatusColor = (status: string) => {
	switch (status) {
		case 'pending':
			return adminColors.tentative;
		case 'in_transit':
			return adminColors.progress;
		case 'delivered':
			return adminColors.success;
		case 'exception':
			return adminColors.danger;
		default:
			return adminColors.info;
	}
};

export const getOrderPartStatusColor = (status: OrderPartStatus) => {
	switch (status) {
		case 'CONFIRMED':
			return adminColors.tentative;
		case 'CAPTURED':
		case 'INVOICED':
			return adminColors.success;
		case 'CANCELLED':
			return adminColors.danger;
		case 'PROCESSING':
		case 'PACKED':
		case 'SHIPPED':
			return adminColors.progress;
		default:
			return adminColors.info;
	}
};

export const getOrderPartStatusText = (status: OrderPartStatus) => {
	switch (status) {
		case 'UNCONFIRMED':
			return 'Ubekreftet';
		case 'CONFIRMED':
			return 'Bekreftet';
		case 'CAPTURED':
			return 'Trukket';
		case 'CANCELLED':
			return 'Kansellert';
		case 'PROCESSING':
			return 'I produksjon';
		case 'PACKED':
			return 'Pakket';
		case 'SHIPPED':
			return 'Levert';
		case 'INVOICED':
			return 'Fakturert';
		default:
			return 'Ukjent status';
	}
};

export const isOrderPartStatusProgressAtLeast = (
	atLeast: OrderPartStatus,
	status: OrderPartStatus,
) => {
	const statusInOrder: OrderPartStatus[] = [
		'CANCELLED',
		'UNCONFIRMED',
		'CONFIRMED',
		'PROCESSING',
		'PACKED',
		'SHIPPED',
		'CAPTURED',
		'INVOICED',
	];

	for (let i = 0; i < statusInOrder.length; i++) {
		const s = statusInOrder[i];
		if (s === atLeast) {
			return true;
		}
		if (s === status) {
			return false;
		}
	}

	return false; // default to false if status not found, should not happen
};

export const getTransactionStatusColor = (status?: TransactionStatus | null) => {
	switch (status) {
		case 'AUTHORIZED':
			return adminColors.tentative;
		case 'CAPTURED':
			return adminColors.success;
		case 'PARTIALLY_CAPTURED':
		case 'PARTIALLY_REFUNDED':
		case 'PARTIALLY_CAPTURED_REFUNDED':
			return adminColors.progress;
		case 'DECLINED':
		case 'FAILED':
		case 'ON_HOLD':
		case 'AUTHORIZATION_VOIDED':
			return adminColors.danger;
		default:
			return adminColors.info;
	}
};

export const getTransactionStatusText = (status?: TransactionStatus | null) => {
	switch (status) {
		case undefined:
		case null:
			return 'Ingen transaksjon';
		case 'AUTHORIZED':
			return 'Reservert';
		case 'CAPTURED':
			return 'Trukket';
		case 'REFUNDED':
			return 'Refundert';
		case 'PARTIALLY_CAPTURED':
			return 'Delvis trukket';
		case 'PARTIALLY_REFUNDED':
			return 'Delvis refundert';
		case 'PARTIALLY_CAPTURED_REFUNDED':
			return 'Delvis trukket og refundert';
		case 'ON_HOLD':
			return 'Pauset';
		case 'DECLINED':
			return 'Avvist';
		case 'FAILED':
			return 'Feilet';
		case 'AUTHORIZATION_VOIDED':
			return 'Kansellert';
		default:
			return 'Ukjent status';
	}
};

export const getLineStatusText = (status: LineStatus) => {
	switch (status) {
		case 'INITIALIZE':
			return 'Avventer betaling';
		case 'AUTHORIZE':
			return 'Reservert';
		case 'CAPTURE':
			return 'Trukket';
		case 'REFUND':
			return 'Refundert';
		case 'VOID':
			return 'Kansellert';
		default:
			return 'Ukjent status';
	}
};

export const getLineStatusColor = (status: LineStatus) => {
	switch (status) {
		case 'AUTHORIZE':
			return adminColors.tentative;
		case 'CAPTURE':
			return adminColors.success;
		case 'VOID':
			return adminColors.danger;
		default:
			return adminColors.info;
	}
};

/**
 * Sorts and maps transaction events to line statuses.
 */
export const getLineStatusMap = (transaction?: Transaction | null) => {
	const map = new Map<string, LineStatus>();

	const events = transaction?.events?.sort((a, b) => Number(a.id) - Number(b.id));

	events?.forEach(({items, event: status}) => {
		items?.forEach((item) => {
			map.set(item.line_id, status);
		});
	});

	return map;
};
